import React, { useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Paper,
    Stack,
    Typography,
    Alert,
    LinearProgress,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { emissionBulkInsert } from "../../library/api/emission";

function hasMeaningfulValue(v) {
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim() !== "";
    if (typeof v === "number") return true;
    if (typeof v === "boolean") return true;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return String(v).trim() !== "";
}

function pickColumnsWithData(rows, preferredOrder = []) {
    const safeRows = Array.isArray(rows) ? rows : [];
    if (safeRows.length === 0) return [];

    // collect keys that have at least one meaningful value
    const keysWithData = new Set();
    for (const r of safeRows) {
        if (!r || typeof r !== "object") continue;
        for (const k of Object.keys(r)) {
            if (hasMeaningfulValue(r[k])) keysWithData.add(k);
        }
    }

    // stable ordering: preferred first, then the rest (alphabetical)
    const preferred = preferredOrder.filter((k) => keysWithData.has(k));
    const rest = Array.from(keysWithData)
        .filter((k) => !preferred.includes(k))
        .sort((a, b) => a.localeCompare(b));

    return [...preferred, ...rest];
}


function formatCellValue(v) {
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number") return String(v);
    if (typeof v === "boolean") return v ? "true" : "false";
    try {
        return JSON.stringify(v);
    } catch {
        return String(v);
    }
}

function RowsTable({ title, columns, rows, maxHeight = 520, minWidth = 900 }) {
    const safeCols = Array.isArray(columns) ? columns : [];
    const safeRows = Array.isArray(rows) ? rows : [];

    return (
        <Box>
            {title ? (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {title}
                </Typography>
            ) : null}

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2, overflow: "auto", maxHeight }}
            >
                <Table stickyHeader size="small" sx={{ minWidth }}>
                    <TableHead>
                        <TableRow>
                            {safeCols.map((col) => (
                                <TableCell key={col} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                    {col}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {safeRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={safeCols.length || 1}>
                                    <Typography variant="body2" color="text.secondary">
                                        No rows
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            safeRows.map((row, idx) => (
                                <TableRow key={row?.__excel_row ?? row?.excel_row ?? idx} hover>
                                    {safeCols.map((col) => (
                                        <TableCell key={`${idx}-${col}`} sx={{ verticalAlign: "top" }}>
                                            {formatCellValue(row?.[col])}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

/**
 * Flatten calculations into table rows:
 * Each item looks like: { excel_row, calc: { ... } }
 * We'll show key highlights + formula info.
 */
function buildCalcTableRows(calculations) {
    const safe = Array.isArray(calculations) ? calculations : [];
    return safe.map((c) => {
        const calc = c?.calc || {};
        const factor = calc?.factor || {};
        const unit = calc?.unit || {};
        const q = calc?.quantities || {};
        const f = calc?.formula || {};

        return {
            excel_row: c?.excel_row ?? "",
            categoryNo: calc?.categoryNo ?? "",
            categoryName: calc?.categoryName ?? "",
            factor_key: factor?.key ?? "",
            factor_value: factor?.value ?? "",
            unit_raw: unit?.raw ?? "",
            qty: q?.qty ?? "",
            distance: q?.distance ?? "",
            weight: q?.weight ?? "",
            lossRate: q?.lossRate ?? "",
            formulaName: f?.formulaName ?? "",
            inputs: f?.inputs ? JSON.stringify(f.inputs) : "",
            emission_factor_value: f?.emission_factor_value ?? "",
            emission_value: f?.emission_value ?? "",
        };
    });
}

function EmissionBulkInsertResponseTables({ responseJson }) {
    const data = responseJson?.data;
    const sheets = Array.isArray(data?.sheets) ? data.sheets : [];

    const summaryRows = useMemo(() => {
        return sheets.map((s, i) => ({
            idx: i + 1,
            categoryNo: s?.categoryNo ?? "",
            name: s?.name || "(Unnamed sheet)",
            rowCount: s?.rowCount ?? (Array.isArray(s?.rows) ? s.rows.length : 0),
            columnCount: Array.isArray(s?.columns) ? s.columns.length : 0,
            calcCount: Array.isArray(s?.calculations) ? s.calculations.length : 0,
            errorCount: Array.isArray(s?.calc_errors) ? s.calc_errors.length : 0,
            truncated: !!s?.truncated,
        }));
    }, [sheets]);

    if (!responseJson) return null;

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Response (Tables)
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>
                <b>File:</b> {data?.file_name || "-"} &nbsp;•&nbsp; <b>Sheet count:</b>{" "}
                {data?.sheet_count ?? sheets.length}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Sheets Summary
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Sheet</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Rows</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Columns</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Calculations</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Calc Errors</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Truncated</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {summaryRows.map((r) => (
                            <TableRow key={r.idx} hover>
                                <TableCell>{r.idx}</TableCell>
                                <TableCell>{r.categoryNo}</TableCell>
                                <TableCell>{r.name}</TableCell>
                                <TableCell>{r.rowCount}</TableCell>
                                <TableCell>{r.columnCount}</TableCell>
                                <TableCell>{r.calcCount}</TableCell>
                                <TableCell>{r.errorCount}</TableCell>
                                <TableCell>{r.truncated ? "true" : "false"}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Sheets (Rows + Calculations)
            </Typography>

            <Box>
                {sheets.map((sheet, idx) => {
                    const calcRows = buildCalcTableRows(sheet?.calculations);
                    const preferredCalcOrder = [
                        "excel_row",
                        "categoryNo",
                        "categoryName",
                        "factor_key",
                        "factor_value",
                        "unit_raw",
                        "qty",
                        "distance",
                        "weight",
                        "lossRate",
                        "formulaName",
                        "inputs",
                        "emission_factor_value",
                        "emission_value",
                    ];

                    const calcColumns = pickColumnsWithData(calcRows, preferredCalcOrder);

                    const calcErrors = Array.isArray(sheet?.calc_errors) ? sheet.calc_errors : [];
                    const calcErrorCols =
                        calcErrors.length > 0
                            ? Array.from(
                                new Set(
                                    calcErrors.flatMap((e) => Object.keys(e || {}))
                                )
                            )
                            : ["message"];

                    const rows = Array.isArray(sheet?.rows) ? sheet.rows : [];
                    const rowCols = Array.isArray(sheet?.columns) ? sheet.columns : [];

                    return (
                        <Accordion key={`${sheet?.name}-${idx}`} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                        {sheet?.name || "(Unnamed sheet)"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        category: {sheet?.categoryNo ?? "-"} • rows:{" "}
                                        {sheet?.rowCount ?? rows.length} • columns: {rowCols.length} • calcs:{" "}
                                        {calcRows.length} • errors: {calcErrors.length} • truncated:{" "}
                                        {String(!!sheet?.truncated)}
                                    </Typography>
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Stack spacing={2}>
                                    <RowsTable
                                        title="Rows (from Excel)"
                                        columns={rowCols}
                                        rows={rows}
                                        maxHeight={420}
                                        minWidth={900}
                                    />

                                    <RowsTable
                                        title="Calculations"
                                        columns={calcColumns}
                                        rows={calcRows}
                                        maxHeight={420}
                                        minWidth={1100}
                                    />

                                    <RowsTable
                                        title="Calculation Errors"
                                        columns={calcErrorCols}
                                        rows={calcErrors}
                                        maxHeight={260}
                                        minWidth={900}
                                    />
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        </Paper>
    );
}

export default function EmissionBulkInsertPage() {
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [responseJson, setResponseJson] = useState(null);

    const handlePickFile = () => fileInputRef.current?.click();

    const handleFileChange = (e) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
        setError("");
        setResponseJson(null);
    };

    const getErrMsg = (err) => {
        return (
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Upload failed."
        );
    };

    const handleUpload = async () => {
        if (!file) {
            setError("Please choose an .xlsx file first.");
            return;
        }

        setUploading(true);
        setError("");
        setResponseJson(null);

        try {
            const form = new FormData();
            form.append("attachment", file);

            const json = await emissionBulkInsert(form);

            if (!json || json?.ok === false) {
                throw new Error(json?.message || "Upload failed.");
            }

            setResponseJson(json);
        } catch (err) {
            setError(getErrMsg(err));
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1300, mx: "auto" }}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                Emission Bulk Insert
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload an Excel file (.xlsx). It will be sent to <b>/emission-bulk-insert</b> as
                multipart form-data with key <b>attachment</b>.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />

                    <Button variant="outlined" onClick={handlePickFile} disabled={uploading}>
                        Choose Excel
                    </Button>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                            Selected file
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={file?.name || ""}
                        >
                            {file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "No file selected"}
                        </Typography>
                    </Box>

                    <Button variant="contained" onClick={handleUpload} disabled={!file || uploading}>
                        {uploading ? "Uploading..." : "Upload"}
                    </Button>
                </Stack>

                {uploading && <LinearProgress sx={{ mt: 2 }} />}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </Paper>

            {responseJson && <EmissionBulkInsertResponseTables responseJson={responseJson} />}
        </Box>
    );
}
